/**
 * Super Admin — Frontend PDF generation.
 *
 * Mirrors apps/user-tenant/src/lib/pdfGeneration.ts but targets the
 * super-admin data endpoint (which has no tenant-ownership check). Uses the
 * same `InvoicePdfDocument` component so the downloaded PDF is byte-equivalent
 * to the one a tenant admin would download from the User Tenant app.
 */
import { createElement } from 'react';
import type { ReactElement } from 'react';
import { pdf, type DocumentProps } from '@react-pdf/renderer';
import type { InvoicePdfData } from '@respectly/shared-types';
import { InvoicePdfDocument } from '../components/pdf/InvoicePdfDocument';

async function authedFetch(url: string, getToken: () => Promise<string | null>): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error('Authentication error. Please refresh and try again.');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message);
  }
  return res;
}

function deliverPdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-{2,}/g, '-').slice(0, 60);
}

export async function generateInvoicePdf(
  invoiceId: string,
  getAccessToken: () => Promise<string | null>,
): Promise<void> {
  const res = await authedFetch(
    `${import.meta.env.VITE_API_URL ?? ''}/api/super-admin/billing/invoices/${invoiceId}/pdf-data`,
    getAccessToken,
  );
  const { data } = (await res.json()) as { data: InvoicePdfData };

  const element = createElement(InvoicePdfDocument, { data }) as ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();

  const filename = `invoice-${sanitizeFilename(data.invoice.invoiceNumber)}.pdf`;
  deliverPdf(blob, filename);
}
